// User types
export interface Usuario {
  id: string;
  nome: string;
  email: string;
  telefone?: string;
  whatsapp?: string;
  avatar?: string;
  tipo: 'comprador' | 'vendedor' | 'admin';
  plano?: Plano;
  verificado: boolean;
  dataCriacao: string;
  dataAtualizacao: string;
  endereco?: Endereco;
  avaliacaoMedia?: number;
  totalAvaliacoes?: number;
}

export interface Endereco {
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
}

export interface Plano {
  id: string;
  nome: 'gratuito' | 'basico' | 'profissional' | 'premium';
  preco: number;
  limiteAnuncios: number;
  limiteFotos: number;
  destaque: boolean;
  suporte: string;
  validoAte: string;
}

// Auth types
export interface LoginCredentials {
  email: string;
  senha: string;
}

export interface RegisterData {
  nome: string;
  email: string;
  senha: string;
  confirmarSenha: string;
  telefone?: string;
  tipo: 'comprador' | 'vendedor';
  aceitouTermos: boolean;
}

export interface AuthResponse {
  token: string;
  usuario: Usuario;
}

// Anuncio types
export interface Anuncio {
  id: string;
  titulo: string;
  descricao: string;
  preco: number;
  precoNegociavel: boolean;
  categoria: Categoria;
  subcategoria?: string;
  status: 'ativo' | 'pausado' | 'vendido' | 'expirado' | 'pendente';
  destaque: boolean;
  visualizacoes: number;
  favoritosCount: number;
  imagens: ImagemAnuncio[];
  vendedor: Usuario;
  localizacao: Localizacao;
  caracteristicas: Caracteristica[];
  dataCriacao: string;
  dataAtualizacao: string;
  dataExpiracao?: string;
}

export interface ImagemAnuncio {
  id: string;
  url: string;
  thumbnail?: string;
  principal: boolean;
  ordem: number;
}

export interface Localizacao {
  cidade: string;
  estado: string;
  cep?: string;
  latitude?: number;
  longitude?: number;
}

export interface Caracteristica {
  nome: string;
  valor: string;
}

export type Categoria = 
  | 'cavalos'
  | 'bovinos'
  | 'equipamentos'
  | 'racoes'
  | 'medicamentos'
  | 'servicos'
  | 'imoveis'
  | 'veiculos'
  | 'outros';

export interface CategoriaInfo {
  id: Categoria;
  nome: string;
  icone: string;
  subcategorias: string[];
}

export const CATEGORIAS: CategoriaInfo[] = [
  {
    id: 'cavalos',
    nome: 'Cavalos',
    icone: '🐴',
    subcategorias: ['Quarto de Milha', 'Mangalarga', 'Crioulo', 'Árabe', 'Paint Horse', 'Appaloosa', 'Outros']
  },
  {
    id: 'bovinos',
    nome: 'Bovinos',
    icone: '🐄',
    subcategorias: ['Nelore', 'Angus', 'Brahman', 'Senepol', 'Gir', 'Girolando', 'Outros']
  },
  {
    id: 'equipamentos',
    nome: 'Equipamentos',
    icone: '🔧',
    subcategorias: ['Selas', 'Arreios', 'Cercas', 'Comedouros', 'Bebedouros', 'Tratores', 'Outros']
  },
  {
    id: 'racoes',
    nome: 'Rações e Suplementos',
    icone: '🌾',
    subcategorias: ['Ração Equina', 'Ração Bovina', 'Sal Mineral', 'Suplementos', 'Feno', 'Outros']
  },
  {
    id: 'medicamentos',
    nome: 'Medicamentos',
    icone: '💊',
    subcategorias: ['Vacinas', 'Vermífugos', 'Anti-inflamatórios', 'Antibióticos', 'Outros']
  },
  {
    id: 'servicos',
    nome: 'Serviços',
    icone: '🛠️',
    subcategorias: ['Veterinário', 'Ferrador', 'Adestramento', 'Transporte', 'Inseminação', 'Outros']
  },
  {
    id: 'imoveis',
    nome: 'Imóveis Rurais',
    icone: '🏡',
    subcategorias: ['Fazendas', 'Sítios', 'Chácaras', 'Haras', 'Terrenos', 'Outros']
  },
  {
    id: 'veiculos',
    nome: 'Veículos',
    icone: '🚜',
    subcategorias: ['Caminhões', 'Trailers', 'Reboques', 'Carretinhas', 'Outros']
  },
  {
    id: 'outros',
    nome: 'Outros',
    icone: '📦',
    subcategorias: ['Diversos']
  }
];

// Filtros
export interface FiltrosAnuncio {
  busca?: string;
  categoria?: Categoria;
  subcategoria?: string;
  precoMin?: number;
  precoMax?: number;
  estado?: string;
  cidade?: string;
  ordenar?: 'recentes' | 'preco_asc' | 'preco_desc' | 'relevancia';
  pagina?: number;
  limite?: number;
}

// Anuncio Form Data
export interface AnuncioFormData {
  titulo: string;
  descricao: string;
  preco: number;
  precoNegociavel: boolean;
  categoria: Categoria | '';
  subcategoria: string;
  cidade: string;
  estado: string;
  cep: string;
  caracteristicas: Caracteristica[];
  imagens: File[];
}

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  pagina: number;
  limite: number;
  totalPaginas: number;
}

// Chat types
export interface Conversa {
  id: string;
  participantes: Usuario[];
  anuncio?: Anuncio;
  ultimaMensagem?: Mensagem;
  naoLidas: number;
  dataCriacao: string;
  dataAtualizacao: string;
}

export interface Mensagem {
  id: string;
  conversaId: string;
  remetente: Usuario;
  conteudo: string;
  tipo: 'texto' | 'imagem' | 'oferta';
  lida: boolean;
  dataCriacao: string;
  oferta?: {
    valor: number;
    status: 'pendente' | 'aceita' | 'recusada';
  };
}

// Favorito
export interface Favorito {
  id: string;
  anuncio: Anuncio;
  dataCriacao: string;
}

// Avaliacao
export interface Avaliacao {
  id: string;
  avaliador: Usuario;
  avaliado: Usuario;
  anuncio?: Anuncio;
  nota: number;
  comentario: string;
  dataCriacao: string;
}

// Dashboard stats
export interface DashboardStats {
  totalAnuncios: number;
  anunciosAtivos: number;
  totalVisualizacoes: number;
  totalFavoritos: number;
  mensagensNaoLidas: number;
  vendasMes: number;
  faturamentoMes: number;
  avaliacaoMedia: number;
}

export interface AdminStats extends DashboardStats {
  totalUsuarios: number;
  novosUsuariosMes: number;
  totalTransacoes: number;
  anunciosPendentes: number;
}

// API Error
export interface ApiError {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

// Estados brasileiros
export const ESTADOS_BRASIL = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
];
